import { assetActivityPath } from '../../assets/assetUtils';

/**
 * Pure navigation decision logic for the Issue detail component.
 *
 * Extracted from IssueBase so it can be unit-tested without React overhead.
 * The caller is responsible for executing the returned slide action and navigation.
 */

/** Describes what navigation should happen when the user presses back in Issue. */
export type BackRoutingDecision =
  | {
      /** Collapse to the leftmost single column, then navigate to the activities list. */
      slide: 'SINGLE';
      url: string;
      state: { skipAutoSelect: true };
    }
  | {
      /** Slide back one column and navigate to the derived parent URL. */
      slide: 'PREV';
      url: string;
    };

/**
 * Computes the back-navigation destination for an Issue panel.
 *
 * Decision table (segments = pathname.split('/').filter(Boolean)):
 *
 *   URL (segments)                                                segs  action
 *   ----------------------------------------------------------   ----  ------
 *   /activities/123                                               2     → activities list (SINGLE)
 *   /activities/123/edit                                          3     → activities list (SINGLE)
 *   /activities/123/asset/456                                     4     → activities list (SINGLE)
 *   /assets/456/activity/123                                      4     → /assets/456       (PREV, -2)
 *   /assets/456/activity/123/edit                                 5     → /assets/456       (PREV, -3)
 *   /activities/123/asset/456/activity/789                        6     → /activities/123/asset/456 (PREV, -2)
 *   /activities/123/asset/456/activity/789/edit                   7     → /activities/123/asset/456 (PREV, -3)
 *
 * Rules:
 *   ≤ 2 segments → activities list
 *     3 activities segments → activities list
 *   4+ segments, even count → remove 2 (paired :type/:id leaf)
 *   5+ segments, odd count  → remove 3 (action sub-page — skip past :id + action)
 *
 * @param pathname       - Current `location.pathname`
 * @param fromParam      - Value of the `from` query-string param (or null)
 * @param activityRoot   - The root path segment for activities (e.g. 'activities')
 * @param allActivitiesQueryValue - The query value that signals "came from all-activities list"
 */
export function computeBackRoute(
  pathname: string,
  fromParam: string | null,
  activityRoot: string,
  allActivitiesQueryValue: string
): BackRoutingDecision {
  const segments = pathname.split('/').filter(Boolean);

  // Special case: arrived via the "all activities" list — go back to the all-activities view.
  if (fromParam === allActivitiesQueryValue) {
    const url = '/' + segments.slice(0, -1).concat('all').join('/');
    return { slide: 'PREV', url };
  }

  if (segments.length <= 2) {
    // At the activity detail home page (/activities/:id) — go back to the list.
    return {
      slide: 'SINGLE',
      url: `/${activityRoot}`,
      state: { skipAutoSelect: true },
    };
  }

  if (
    segments[0] === activityRoot &&
    segments.length === 3 &&
    segments[2] === 'edit'
  ) {
    // In the activities shell, /activities/:issueId/edit shows B+C on
    // screen while A is hidden left. B's back button must close both B+C.
    return {
      slide: 'SINGLE',
      url: `/${activityRoot}`,
      state: { skipAutoSelect: true },
    };
  }

  if (
    segments[0] === activityRoot &&
    segments.length === 4 &&
    segments[2] === 'asset'
  ) {
    // In the activities shell, /activities/:issueId/asset/:assetId shows
    // B+C on screen while A is hidden left. B's back button must close both.
    return {
      slide: 'SINGLE',
      url: `/${activityRoot}`,
      state: { skipAutoSelect: true },
    };
  }

  // Determine how many trailing segments to drop.
  let segmentsToRemove: number;
  if (segments.length === 3) {
    // Non-activities shallow action page: go up 1 to the detail page.
    segmentsToRemove = 1;
  } else {
    // Deeper paths: even depth = paired id (drop 2), odd depth = action page (drop 3).
    segmentsToRemove = segments.length % 2 === 0 ? 2 : 3;
  }

  const targetSegments = segments.slice(0, segments.length - segmentsToRemove);

  if (targetSegments.length < 2) {
    // Would land at or above root — fall back to the activities list.
    return {
      slide: 'SINGLE',
      url: `/${activityRoot}`,
      state: { skipAutoSelect: true },
    };
  }

  return { slide: 'PREV', url: '/' + targetSegments.join('/') };
}

/**
 * Destination for the "View related routine / report" link on a routine
 * message, which jumps to the linked activity on the same asset.
 *
 * Inside an asset stack the activity detail is `activity/:issueId` under the
 * asset base path — `/assets/:assetId`, or `…/asset/:assetId` for the
 * activities, digest and workspace contexts. That base path is recovered from
 * the pathname rather than rebuilt from route params or a prop, because the
 * panel the user is looking at is the only thing that says which stack the link
 * has to stay inside: `AssetRouteContext.basePath` is the activity's own path
 * in the activity-edit panel, and `isComingFromActivities` is passed there even
 * when the panel is mounted under `/assets/:assetId`. A pathname carrying no
 * asset segment is the activities shell, which owns its own detail route.
 *
 * `activities/:issueId` (plural) also still matches, as a legacy child of
 * `/assets/:assetId`, but that parent renders no outlet, so navigating there
 * silently lands on the asset profile instead of the activity.
 *
 * @param pathname     - Current `location.pathname`
 * @param search       - Current `location.search`
 * @param targetIssueId - Issue to open
 * @param assetId      - Asset the current issue belongs to, when it has one
 * @param activityRoot - The root path segment for activities (e.g. 'activities')
 * @param assetRoot    - The root path segment for assets (e.g. 'assets')
 */
export function computeLinkedIssueRoute(
  pathname: string,
  search: string,
  targetIssueId: string,
  assetId: string | undefined,
  activityRoot: string,
  assetRoot: string
): string {
  const assetBasePath = assetId
    ? findAssetBasePath(pathname, assetId, assetRoot)
    : undefined;

  if (!assetBasePath) {
    return `/${activityRoot}/${targetIssueId}`;
  }

  // Carry `from` across so the destination keeps the left panel the user is
  // looking at (activity list vs. asset card) instead of swapping it out.
  const from = new URLSearchParams(search).get('from');
  const query = from ? `?from=${encodeURIComponent(from)}` : '';

  return assetActivityPath(assetBasePath, targetIssueId, query);
}

/**
 * The asset-scoped prefix of `pathname`, or undefined if it has none —
 * `/assets/:assetId` at the root, or `…/asset/:assetId` nested under the
 * activities, digest and workspace stacks.
 *
 * Matched on whole segments, never by substring: `/asset/:assetId` is a prefix
 * of the same segment carrying more characters, so a substring search hands
 * back a base path pointing at a DIFFERENT asset. Ids are UUIDs today, which
 * cannot prefix one another, but nothing here enforces that.
 */
function findAssetBasePath(
  pathname: string,
  assetId: string,
  assetRoot: string
): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  const assetIdIndex = segments.findIndex(
    (segment, index) =>
      segments[index + 1] === assetId &&
      (segment === 'asset' || (segment === assetRoot && index === 0))
  );

  return assetIdIndex >= 0
    ? `/${segments.slice(0, assetIdIndex + 2).join('/')}`
    : undefined;
}
